<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['removeAttachment']);
const { t } = useI18n();

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isVoiceMessage);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isVoiceMessage)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const fileData = attachment => attachment?.resource || {};

const mimeType = attachment => {
  const file = fileData(attachment);
  return file.content_type || file.type || '';
};

const isTypeImage = attachment => mimeType(attachment).includes('image');
const isTypeVideo = attachment => mimeType(attachment).includes('video');
const isTypeAudio = attachment => mimeType(attachment).includes('audio');
const isTypePdf = attachment => mimeType(attachment).includes('pdf');

const formatFileSize = attachment => {
  const file = fileData(attachment);
  const size = file.byte_size || file.size || 0;
  return formatBytes(size, 0);
};

const fileName = attachment => {
  const file = fileData(attachment);
  return file.filename || file.name;
};

const fileExtension = attachment => {
  const name = fileName(attachment);
  const extension = name.split('.').pop();

  if (!extension || extension === name) {
    return t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.FILE');
  }

  return extension.toUpperCase();
};

const previewUrl = attachment => {
  return (
    attachment?.thumb ||
    attachment?.resource?.data_url ||
    attachment?.resource?.dataUrl ||
    ''
  );
};

const fileTypeLabel = attachment => {
  const type = mimeType(attachment);

  if (type.includes('image')) {
    return t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.IMAGE');
  }
  if (type.includes('video')) {
    return t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.VIDEO');
  }
  if (type.includes('audio')) {
    return t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.AUDIO');
  }
  if (type.includes('pdf')) {
    return t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.PDF');
  }

  return t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.FILE');
};

const fileIcon = attachment => {
  if (isTypeAudio(attachment)) return 'i-ph-waveform';
  if (isTypeVideo(attachment)) return 'i-ph-video-camera';
  if (isTypePdf(attachment)) return 'i-lucide-file-text';
  return 'i-lucide-file';
};
</script>

<template>
  <div class="grid max-h-80 gap-3 overflow-auto lg:grid-cols-2">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id || `${fileName(attachment)}-${index}`"
      class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3 shadow-sm"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="inline-flex rounded-full bg-n-alpha-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-n-slate-11"
            >
              {{ fileTypeLabel(attachment) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ formatFileSize(attachment) }}
            </span>
            <span
              v-if="!isTypeImage(attachment) && !isTypeVideo(attachment) && !isTypeAudio(attachment)"
              class="inline-flex rounded-full border border-n-weak px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-n-slate-11"
            >
              {{ fileExtension(attachment) }}
            </span>
          </div>
          <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
            {{ fileName(attachment) }}
          </p>
        </div>
        <Button ghost slate xs icon="i-lucide-x" @click="onRemoveAttachment(index)" />
      </div>

      <img
        v-if="isTypeImage(attachment)"
        :src="previewUrl(attachment)"
        :alt="fileName(attachment)"
        class="h-40 w-full rounded-xl bg-n-slate-3 object-cover"
      />

      <div
        v-else-if="isTypeVideo(attachment)"
        class="space-y-3"
      >
        <video
          :src="previewUrl(attachment)"
          class="h-40 w-full rounded-xl bg-n-slate-3 object-cover"
          controls
          preload="metadata"
        />
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <span
            class="inline-flex size-7 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-ph-video-camera" class="size-4" />
          </span>
          {{ $t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.VIDEO') }}
        </div>
      </div>

      <div
        v-else-if="isTypeAudio(attachment)"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-3"
      >
        <div class="mb-3 flex items-center gap-3">
          <span
            class="inline-flex size-10 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-ph-waveform" class="size-5" />
          </span>
          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.AUDIO') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ formatFileSize(attachment) }}
            </p>
          </div>
        </div>
        <audio
          :src="previewUrl(attachment)"
          class="w-full"
          controls
          preload="metadata"
        />
      </div>

      <div
        v-else
        class="flex items-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-1 p-4"
      >
        <span
          class="inline-flex size-12 flex-shrink-0 items-center justify-center rounded-xl bg-n-slate-3 text-n-slate-11"
        >
          <Icon :icon="fileIcon(attachment)" class="size-5" />
        </span>
        <div class="min-w-0 space-y-1">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ fileTypeLabel(attachment) }}
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ fileExtension(attachment) }} • {{ formatFileSize(attachment) }}
          </p>
          <p class="mb-0 truncate text-xs text-n-slate-10">
            {{ fileName(attachment) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
